import React, { useEffect, useRef, useCallback } from "react";
import { playBufferThroughSharedDestination } from "../utils/audioRouting";

/**
 * CustomAvatarDisplay.jsx — WebRTC-feel streaming via Canvas → MediaStream
 *
 * Instead of rendering JPEGs to an <img> tag (slideshow feel), this component:
 * 1. Draws incoming JPEG frames onto a hidden <canvas> at 25fps
 * 2. Captures the canvas as a MediaStream via captureStream(25)
 * 3. Feeds that MediaStream into a <video> element
 *
 * Result: browser treats it exactly like a WebRTC video stream —
 * smooth playback, no flicker, real avatar feeling.
 *
 * Props:
 *   frameData      — base64 JPEG string (new value triggers queuing)
 *   frameIndex     — increments each frame (used as queue trigger)
 *   isAISpeaking   — controls speaking indicator
 *   audioChunkData — base64 WAV chunk to play in sync (optional)
 *   avatarImageUrl — URL of the static face image shown while listening
 */

const FPS = 25;
const FRAME_INTERVAL_MS = 1000 / FPS; // 40ms per frame

const CustomAvatarDisplay = ({
  frameData,
  frameIndex,
  isAISpeaking,
  audioChunkData,
  avatarImageUrl,
}) => {
  // ── Refs ────────────────────────────────────────────────────────────────────
  const videoRef        = useRef(null);   // <video> that plays the canvas stream
  const canvasRef       = useRef(null);   // hidden canvas we draw frames onto
  const frameQueueRef   = useRef([]);     // incoming frames waiting to be drawn
  const renderTimerRef  = useRef(null);   // setInterval handle for frame drain
  const streamRef       = useRef(null);   // MediaStream captured from canvas
  const isSetupDoneRef  = useRef(false);  // guard against double-setup
  const lastFrameRef    = useRef(null);   // last rendered frame (for freeze on pause)

  // ── Setup: canvas → MediaStream → <video> ────────────────────────────────
  const setupCanvasStream = useCallback(() => {
    if (isSetupDoneRef.current) return;
    const canvas = canvasRef.current;
    const video  = videoRef.current;
    if (!canvas || !video) return;

    try {
      // Capture canvas at 25fps as a live MediaStream
      const stream = canvas.captureStream(FPS);
      streamRef.current = stream;
      video.srcObject = stream;
      video.play().catch((e) => {
        console.warn("[CustomAvatarDisplay] video.play() blocked:", e.message);
      });

      // Draw the static avatar face onto canvas immediately (listening state)
      _drawStaticFace();

      isSetupDoneRef.current = true;
      console.log("✅ [CustomAvatarDisplay] Canvas stream → <video> ready");
    } catch (e) {
      console.error("[CustomAvatarDisplay] captureStream failed:", e);
    }
  }, []); // eslint-disable-line

  // ── Draw static face image onto canvas (listening state) ─────────────────
  const _drawStaticFace = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    if (avatarImageUrl) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        canvas.width  = img.naturalWidth  || 512;
        canvas.height = img.naturalHeight || 512;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      };
      img.src = avatarImageUrl;
    } else {
      // Fallback: dark gradient placeholder
      canvas.width  = 512;
      canvas.height = 512;
      const gradient = ctx.createRadialGradient(256, 200, 40, 256, 256, 260);
      gradient.addColorStop(0, "#2d1b69");
      gradient.addColorStop(1, "#0d0d1a");
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, 512, 512);
    }
  }, [avatarImageUrl]);

  // ── Mount: setup canvas stream ────────────────────────────────────────────
  useEffect(() => {
    setupCanvasStream();
  }, [setupCanvasStream]);

  // ── Queue incoming frames ─────────────────────────────────────────────────
  useEffect(() => {
    if (!frameData) return;
    frameQueueRef.current.push(frameData);

    // Auto-start drain loop the moment first frame arrives
    // Don't wait for isAISpeaking — frames ARE the signal that speaking started
    if (!renderTimerRef.current) {
      console.log("▶️ [CustomAvatarDisplay] First frame arrived — starting drain");
      renderTimerRef.current = setInterval(() => {
        const frame = frameQueueRef.current.shift();
        if (!frame) return;

        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        const img = new Image();
        img.onload = () => {
          if (canvas.width !== img.naturalWidth && img.naturalWidth > 0) {
            canvas.width  = img.naturalWidth;
            canvas.height = img.naturalHeight;
          }
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          lastFrameRef.current = frame;
        };
        img.src = frame.startsWith("data:") ? frame : `data:image/jpeg;base64,${frame}`;
      }, FRAME_INTERVAL_MS);
    }
  }, [frameData, frameIndex]);

  // ── Stop drain when speaking ends ────────────────────────────────────────
  useEffect(() => {
    if (!isAISpeaking && renderTimerRef.current) {
      // Small delay — let last frames finish rendering before stopping
      setTimeout(() => {
        if (!isAISpeaking) {
          clearInterval(renderTimerRef.current);
          renderTimerRef.current = null;
          frameQueueRef.current = [];
          console.log("⏹️ [CustomAvatarDisplay] Frame drain stopped");
          _drawStaticFace();
        }
      }, 500);
    }
  }, [isAISpeaking, _drawStaticFace]);

  // ── Play incoming audio chunks ─────────────────────────────────────────────
  useEffect(() => {
    if (!audioChunkData?.data) return;

    const playChunk = async () => {
      try {
        const binary = atob(audioChunkData.data);
        const bytes  = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

        const audioCtx = window.__AI_AUDIO_CONTEXT__ || window.__AI_AUDIO_DESTINATION__?.context;
        if (!audioCtx) {
          console.warn("[CustomAvatarDisplay] Shared audio graph unavailable");
          return;
        }

        const decoded = await audioCtx.decodeAudioData(bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength));
        await playBufferThroughSharedDestination({
          audioBuffer: decoded,
          localPlayback: true,
          when: 0,
        });
      } catch (e) {
        console.warn("[CustomAvatarDisplay] Audio chunk play error:", e);
      }
    };

    playChunk();
  }, [audioChunkData]);

  // ── Cleanup on unmount ────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (renderTimerRef.current) clearInterval(renderTimerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      isSetupDoneRef.current = false;
    };
  }, []);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="relative w-full h-full bg-black flex items-center justify-center overflow-hidden">

      {/* Hidden canvas — frames are drawn here */}
      <canvas
        ref={canvasRef}
        style={{ display: "none" }}
        width={512}
        height={512}
      />

      {/* <video> plays the canvas MediaStream — looks like real WebRTC */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted       // audio is played via AudioContext, not this element
        className="w-full h-full object-cover"
        style={{ backgroundColor: "#000" }}
      />

    </div>
  );
};

export default CustomAvatarDisplay;
